import type { HelpTopicsPayload, TicketDetail } from "@noverik/contracts";
import { FileText, Paperclip, Send, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";
import { encodeAttachments } from "../lib/files";
import { RichTextEditor, hasRichTextContent } from "../components/RichTextEditor";

export function NewTicketPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<HelpTopicsPayload | null>(null);
  const [topicId, setTopicId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionPhase, setSubmissionPhase] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    apiRequest<HelpTopicsPayload>("/help-topics")
      .then((payload) => {
        if (!active) return;
        setTopics(payload);

        const defaultId = payload.defaultTopicId
          && payload.items.some((topic) => topic.id === payload.defaultTopicId)
          ? payload.defaultTopicId
          : payload.items[0]?.id;

        if (defaultId) {
          setTopicId(String(defaultId));
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Não foi possível carregar os tópicos de ajuda.",
        );
      })
      .finally(() => {
        if (active) setLoadingTopics(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken) {
      setError("Sua sessão expirou. Entre novamente.");
      return;
    }

    const parsedTopicId = Number(topicId);
    if (!Number.isInteger(parsedTopicId) || parsedTopicId <= 0) {
      setError("Selecione um tópico de ajuda.");
      return;
    }

    if (!hasRichTextContent(message)) {
      setError("Descreva a solicitação antes de criar o chamado.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSubmissionPhase("Preparando anexos...");

    try {
      const attachments = await encodeAttachments(files);
      setSubmissionPhase("Registrando chamado e processando as notificações...");
      const ticket = await apiRequest<TicketDetail>("/tickets", {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          topicId: parsedTopicId,
          subject,
          message,
          attachments,
        },
      });

      navigate("/chamados/" + ticket.number, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : "Não foi possível abrir o chamado.",
      );
    } finally {
      setSubmitting(false);
      setSubmissionPhase("");
    }
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, position) => position !== index));
  };

  return (
    <div className="page new-ticket-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">NOVA SOLICITAÇÃO</span>
          <h1>Abrir novo chamado</h1>
          <p>Conte o que aconteceu e envie as informações necessárias para iniciarmos o atendimento.</p>
        </div>
      </div>

      <form className="ticket-form-card" onSubmit={submit}>
        <div className="ticket-form-section">
          <div className="form-section-heading">
            <span className="meta-icon blue"><FileText size={20} /></span>
            <div>
              <strong>Informações do atendimento</strong>
              <p>Classifique e descreva sua solicitação.</p>
            </div>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Tópico de ajuda</span>
              <select
                value={topicId}
                onChange={(event) => setTopicId(event.target.value)}
                disabled={loadingTopics || submitting}
                required
              >
                <option value="">
                  {loadingTopics ? "Carregando..." : "Selecione um tópico"}
                </option>
                {topics?.items.map((topic) => (
                  <option key={topic.id} value={topic.id}>{topic.name}</option>
                ))}
              </select>
            </label>

            <label className="field form-span">
              <span>Assunto</span>
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Resuma o que você precisa"
                maxLength={255}
                required
              />
            </label>

            <label className="field form-span">
              <span>Descrição</span>
              <RichTextEditor
                value={message}
                onChange={setMessage}
                disabled={submitting}
                minHeight={215}
                label="Descrição da solicitação"
                placeholder="Descreva o problema, o que estava fazendo, mensagens apresentadas e qualquer detalhe que possa ajudar..."
              />
            </label>
          </div>
        </div>

        <div className="ticket-form-section">
          <div className="form-section-heading">
            <span className="meta-icon purple"><Paperclip size={20} /></span>
            <div>
              <strong>Anexos</strong>
              <p>Inclua capturas de tela, documentos ou outros arquivos úteis.</p>
            </div>
          </div>

          <label className="upload-zone">
            <Paperclip size={20} />
            <strong>Selecionar arquivos</strong>
            <span>Até 10 arquivos por envio. Os limites do osTicket serão respeitados.</span>
            <input
              type="file"
              multiple
              onChange={(event) => {
                const selected = Array.from(event.target.files ?? []);
                setFiles((current) => [...current, ...selected].slice(0, 10));
                event.target.value = "";
              }}
            />
          </label>

          {files.length > 0 && (
            <div className="file-list">
              {files.map((file, index) => (
                <div className="file-chip" key={file.name + file.lastModified + index}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>{Math.max(1, Math.round(file.size / 1024))} KB</span>
                  </div>
                  <button type="button" onClick={() => removeFile(index)} aria-label={"Remover " + file.name}>
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="login-message form-error">{error}</div>}

        {submitting && submissionPhase && (
          <div className="ticket-submit-progress" role="status" aria-live="polite">
            <span className="ticket-submit-spinner" aria-hidden="true" />
            <span>{submissionPhase}</span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="button secondary"
            disabled={submitting}
            onClick={() => navigate(-1)}
          >
            Cancelar
          </button>
          <button className="button primary" disabled={submitting}>
            <Send size={17} />
            {submitting ? "Abrindo chamado..." : "Criar chamado"}
          </button>
        </div>
      </form>
    </div>
  );
}
